import { useState, useEffect, useRef, useId } from "react";

// Classic bulb + rising-liquid thermometer for Discovery cards.
export default function GoalThermometer({ raised = 0, amount = 0, title }) {
  const pct = amount > 0 ? Math.min(100, (raised / amount) * 100) : 0;
  const almost = pct >= 80 && pct < 100;
  const complete = pct >= 100;
  const [fill, setFill] = useState(0);
  const ref = useRef(null);
  const gradId = `therm-grad-${useId().replace(/:/g, "")}`;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setTimeout(() => setFill(pct), 120); io.disconnect(); } },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [pct]);

  // Thermometer geometry (SVG viewBox 24 x 96)
  const stemTop = 8, stemBottom = 70, stemH = stemBottom - stemTop;
  const liquidTop = stemBottom - (stemH * fill) / 100;

  return (
    <div
      ref={ref}
      data-testid="goal-thermometer"
      className={`mt-3 flex items-center gap-3 rounded-2xl p-2.5 transition-all duration-300 ${
        complete
          ? "bg-sage/15 ring-1 ring-sage/40"
          : almost
          ? "bg-penny/15 ring-1 ring-penny/50 shadow-[0_0_18px_-4px_rgba(242,169,139,0.7)]"
          : "bg-secondary/60"
      }`}
    >
      <svg width="24" height="72" viewBox="0 0 24 96" className="shrink-0 overflow-visible">
        <defs>
          <linearGradient id={gradId} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#E87FA3" />
            <stop offset="55%" stopColor="#F2A98B" />
            <stop offset="100%" stopColor="#F2CE63" />
          </linearGradient>
        </defs>
        {/* track */}
        <rect x="8" y={stemTop} width="8" height={stemH} rx="4" className="fill-secondary" />
        <circle cx="12" cy="80" r="11" className="fill-secondary" />
        {/* liquid */}
        <circle cx="12" cy="80" r="8" fill={`url(#${gradId})`} />
        <rect
          x="10"
          y={liquidTop}
          width="4"
          height={stemBottom - liquidTop + 4}
          rx="2"
          fill={`url(#${gradId})`}
          style={{ transition: "y 1.1s cubic-bezier(0.34,1.56,0.64,1), height 1.1s cubic-bezier(0.34,1.56,0.64,1)" }}
        />
        {/* outline */}
        <rect x="8" y={stemTop} width="8" height={stemH} rx="4" fill="none" className="stroke-bronze/25" strokeWidth="1.5" />
        <circle cx="12" cy="80" r="11" fill="none" className="stroke-bronze/25" strokeWidth="1.5" />
      </svg>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-1">
          <span className="font-mono text-[10px] uppercase tracking-widest text-copper truncate">
            {title || "Goal"}
          </span>
          <span data-testid="goal-thermometer-pct" className="text-xs font-bold text-bronze">{Math.round(pct)}%</span>
        </div>
        <p className="text-[11px] text-foreground/70 mt-0.5">
          ${raised.toFixed(0)} <span className="text-muted-foreground">of ${amount.toFixed(0)}</span>
        </p>
        {complete ? (
          <span className="inline-block mt-1 text-[10px] font-semibold text-sage">Goal reached! 🎉</span>
        ) : almost ? (
          <span data-testid="almost-there-badge" className="inline-block mt-1 text-[10px] font-semibold text-copper animate-pulse">Almost there! 🔥</span>
        ) : null}
      </div>
    </div>
  );
}
